import { Component, Pipe, PipeTransform, input, output } from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { TaskStatisItem } from '../../message.api.service';

export type MessageItemKind = 'question' | 'module' | 'task';

/**
 * 把超长连笔题号拆成多行展示（如 "2-6" → ["2", "|", "6"]）
 *
 * 数字段之间插入竖线分隔行：首行起始题号，中间行「|」，末行结束题号。
 * 单段题号原样单行。
 */
@Pipe({ name: 'messageNoLines', standalone: true })
export class MessageNoLinesPipe implements PipeTransform {
  transform(no: string | undefined): string[] {
    if (!no) return [];
    const parts = no.split(/[-～~]/).map(s => s.trim());
    if (parts.length <= 1) return [no];
    return parts.reduce<string[]>((acc, part, i) => {
      if (i > 0) acc.push('|');
      acc.push(part);
      return acc;
    }, []);
  }
}

/** 题目/模块/任务导航项类型：有题号 → 题目；无题号有模块 → 模块；皆无 → 任务 */
@Pipe({ name: 'messageItemKind', standalone: true })
export class MessageItemKindPipe implements PipeTransform {
  transform(item: TaskStatisItem | undefined): MessageItemKind {
    if (item?.paperQuestionId) return 'question';
    if (item?.moduleId) return 'module';
    return 'task';
  }
}

/**
 * 题号导航轨（任务消息视图右侧）
 *
 * 输入 statis 列表 + 当前选中下标，点击某题号向外抛出切换事件。
 * 按类型渲染：题目显示题号（超长连笔题号拆多行、项随内容变高）；模块/任务的反馈显示统一图标。带未读红点。
 */
@Component({
  selector: 'fl-task-message-question-nos',
  template: `
    @for (item of statis(); track item.id; let i = $index) {
      <div
        class="relative flex shrink-0 min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-full px-[6px] py-[2px] text-[13px] leading-[1.3] text-neutral-n3 transition-all duration-150 hover:bg-neutral-n6 aria-selected:bg-primary aria-selected:font-medium aria-selected:text-neutral-n9"
        [class.has-unread]="item.unReadCount > 0"
        [attr.aria-selected]="i === selectedIndex()"
        (click)="selected.emit(i)"
      >
        @if ((item | messageItemKind) === 'question') {
          <span class="whitespace-nowrap">
            @for (line of item.no | messageNoLines; track line) {
              @if (line === '|') {
                <span class="block h-[12px] text-center font-bold text-[10px] leading-none text-neutral-n4">{{
                  line
                }}</span>
              } @else {
                <span class="block h-[14px] text-center">{{ line }}</span>
              }
            }
          </span>
        } @else {
          <span nz-icon nzType="file-text" class="text-[14px]"></span>
        }
        @if (item.unReadCount > 0) {
          <i class="absolute right-[2px] top-[2px] h-[8px] w-[8px] rounded-[50%] bg-red-r3"></i>
        }
      </div>
    } @empty {
      <div class="py-[16px] text-center text-neutral-n4">暂无题号</div>
    }
  `,
  styles: [
    `
      :host {
        display: flex;
        height: 100%;
        min-height: 0;
        flex-direction: column;
        gap: 8px;
        align-items: center;
        overflow-y: auto;
        scrollbar-width: none;
      }
      :host::-webkit-scrollbar {
        display: none;
      }
    `,
  ],
  standalone: true,
  imports: [NzIconModule, MessageItemKindPipe, MessageNoLinesPipe],
})
export class TaskMessageQuestionNosComponent {
  /** statis 题号列表 */
  readonly statis = input<TaskStatisItem[]>([]);

  /** 当前选中项的索引 */
  readonly selectedIndex = input(0);

  /** 选中某题 */
  readonly selected = output<number>();
}
